import React, { useCallback, useMemo } from "react";
import { withTranslation } from "react-i18next";
import {
  Chart as ChartJS,
  CategoryScale,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
} from "chart.js";
import { Line } from "react-chartjs-2";
import { SEVERITY_COLORS, SEVERITY_MARKER, SEVERITY_LEGEND } from "../constants/timelineSeverity";
import { detectPeriodGaps } from "../utils/timelineChartUtils";
import { formatTimelinePeriod } from "../utils/timelineFormatters";
import {
  dataPointShowsExpectedBaseline,
  shouldShowTimelineExpectedBaseline,
} from "../../../../../core/constants/anomalyRuleCodes";
import { formatAnomalySeverityLabel } from "../../../../../core/constants/anomalySeverity";
import AnomalyInfoTip from "../../AnomalyInfoTip";

ChartJS.register(CategoryScale, Filler, Legend, LinearScale, LineElement, PointElement, Tooltip);

const MAX_STORYLINE_POINT_RADIUS = 8;
const MAX_TRIANGLE_POINT_RADIUS = 10;

/** Compact numeric label drawn inside chart markers. */
const formatPointLabel = (value) => {
  if (value == null || Number.isNaN(value)) return "";
  const abs = Math.abs(value);
  if (abs >= 1000) {
    return value.toLocaleString(undefined, { maximumFractionDigits: 0 });
  }
  if (Number.isInteger(value)) return String(value);
  return value.toLocaleString(undefined, { maximumFractionDigits: 1 });
};

const getPointStyle = (dp) => {
  if (!dp?.hasAnomaly) {
    return { bg: "#3b82f6", border: "#3b82f6", borderW: 1, radius: 4, style: "circle", hover: 6 };
  }
  const marker = SEVERITY_MARKER[dp.maxSeverity] || SEVERITY_MARKER.HIGH;
  const countBoost = Math.min(2, (dp.anomalyCount || 1) - 1);
  const isTriangle = marker.pointStyle === "triangle";
  const maxRadius = isTriangle ? MAX_TRIANGLE_POINT_RADIUS : MAX_STORYLINE_POINT_RADIUS;
  const radius = Math.min(marker.radius + countBoost, maxRadius);
  return {
    bg: marker.hollow ? "transparent" : marker.color,
    border: marker.color,
    borderW: marker.hollow ? 2 : 1,
    radius,
    style: marker.pointStyle,
    hover: Math.min(radius + 2, maxRadius + 2),
  };
};

const gapLabelPlugin = {
  id: "matGapLabelPlugin",
  afterDatasetsDraw(chart) {
    const gapMidpoints = chart.config.options.plugins?.matGapLabelPlugin?.gapMidpoints;
    if (!gapMidpoints?.length) return;
    const { ctx } = chart;
    const xScale = chart.scales.x;
    const yScale = chart.scales.y;
    const label = chart.config.options.plugins?.matGapLabelPlugin?.label || "";
    gapMidpoints.forEach(({ index, nextIndex }) => {
      const x1 = xScale.getPixelForValue(index);
      const x2 = xScale.getPixelForValue(nextIndex);
      const midX = (x1 + x2) / 2;
      const dataset = chart.data.datasets[0];
      const yRef = dataset.data[index] ?? dataset.data[nextIndex] ?? 0;
      const midY = yScale.getPixelForValue(yRef);
      ctx.save();
      ctx.font = "10px Inter, sans-serif";
      ctx.fillStyle = SEVERITY_COLORS.CRITICAL;
      ctx.textAlign = "center";
      ctx.fillText(label, midX, midY - 8);
      ctx.restore();
    });
  },
};

const getMarkerLabel = (dp) => {
  if (dp.value == null || Number.isNaN(dp.value)) return "";
  return formatPointLabel(dp.value);
};

const getMarkerElement = (chart, index, dp, orphanDatasetIndex) => {
  const primary = chart.getDatasetMeta(0)?.data?.[index];
  const orphan = chart.getDatasetMeta(orphanDatasetIndex)?.data?.[index];

  const isVisible = (el) => el && !el.skip && (el.options?.radius ?? 0) > 0;

  if (dp.value != null && isVisible(primary)) return primary;
  if (dp.value == null && dp.hasAnomaly && isVisible(orphan)) return orphan;
  if (isVisible(primary)) return primary;
  return null;
};

const drawMarkerLabel = (ctx, element, label, style) => {
  if (!label || !element) return;

  const fontSize = style.style === "triangle" ? 10 : 9;
  const yOffset = style.style === "triangle" ? 2 : 0;

  ctx.save();
  ctx.font = `700 ${fontSize}px Inter, sans-serif`;
  ctx.fillStyle = "#ffffff";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(label, element.x, element.y + yOffset);
  ctx.restore();
};

const pointValuePlugin = {
  id: "matPointValuePlugin",
  afterDraw(chart) {
    const pluginOpts = chart.options.plugins?.matPointValuePlugin;
    const points = pluginOpts?.dataPoints;
    const orphanDatasetIndex = pluginOpts?.orphanDatasetIndex ?? 1;
    if (!points?.length) return;

    const { ctx } = chart;
    points.forEach((dp, index) => {
      if (!dp?.hasAnomaly) return;

      const style = getPointStyle(dp);
      if (style.bg === "transparent") return;

      const element = getMarkerElement(chart, index, dp, orphanDatasetIndex);
      const label = getMarkerLabel(dp);
      drawMarkerLabel(ctx, element, label, style);
    });
  },
};

let matChartPluginsRegistered = false;
if (!matChartPluginsRegistered) {
  ChartJS.register(gapLabelPlugin, pointValuePlugin);
  matChartPluginsRegistered = true;
}

const MetricStorylineChart = ({
  t,
  dataPoints,
  ruleCode,
  selectedPeriod,
  onPeriodSelect,
  onRangeSelect,
  rangeAnchorPeriod,
}) => {
  const showBaseline = shouldShowTimelineExpectedBaseline(ruleCode);

  const { gapAfterIndex, gapMidpoints } = useMemo(
    () => detectPeriodGaps(dataPoints),
    [dataPoints]
  );

  const chartBundle = useMemo(() => {
    const pointStyles = dataPoints.map((dp) => getPointStyle(dp));
    const labels = dataPoints.map((d) => formatTimelinePeriod(d.period));

    const expectedValues = showBaseline
      ? dataPoints.map((d) =>
          dataPointShowsExpectedBaseline(ruleCode, d) ? d.expectedValue : null
        )
      : [];
    const hasBaselineData = expectedValues.some((v) => v != null);

    const orphanAnomalyY = dataPoints.map((d) => {
      if (d.hasAnomaly && d.value == null) {
        if (dataPointShowsExpectedBaseline(ruleCode, d)) {
          return d.expectedValue;
        }
        return 0;
      }
      return null;
    });
    const orphanStyles = dataPoints.map((dp) =>
      dp.hasAnomaly && dp.value == null ? getPointStyle(dp) : null
    );

    const actualDataset = {
      label: t("AnomalyDetection.actualValue.label"),
      data: dataPoints.map((d) => (d.value != null ? d.value : null)),
      borderColor: "#3b82f6",
      backgroundColor: "rgba(59, 130, 246, 0.06)",
      pointBackgroundColor: pointStyles.map((s) => s.bg),
      pointBorderColor: pointStyles.map((s) => s.border),
      pointBorderWidth: pointStyles.map((s) => s.borderW),
      pointRadius: pointStyles.map((s) => s.radius),
      pointStyle: pointStyles.map((s) => s.style),
      pointHoverRadius: pointStyles.map((s) => s.hover),
      tension: 0.4,
      fill: true,
      spanGaps: false,
      segment: {
        borderColor: (ctx) => (gapAfterIndex[ctx.p0DataIndex] ? SEVERITY_COLORS.CRITICAL : "#3b82f6"),
        borderDash: (ctx) => (gapAfterIndex[ctx.p0DataIndex] ? [6, 4] : undefined),
      },
    };

    const datasets = [actualDataset];
    let expectedDatasetIndex = -1;

    if (hasBaselineData) {
      expectedDatasetIndex = datasets.length;
      datasets.push({
        label: t("AnomalyDetection.expectedValue.label"),
        data: expectedValues,
        borderColor: "#64748b",
        backgroundColor: "transparent",
        borderDash: [6, 4],
        pointRadius: 3,
        pointHoverRadius: 5,
        pointBackgroundColor: "#94a3b8",
        pointBorderColor: "#94a3b8",
        tension: 0.4,
        fill: false,
        spanGaps: false,
      });
    }

    const orphanDatasetIndex = datasets.length;
    datasets.push({
      label: t("AnomalyDetection.missingDataMarker.label"),
      data: orphanAnomalyY,
      borderColor: "transparent",
      backgroundColor: "transparent",
      showLine: false,
      pointBackgroundColor: orphanStyles.map((s) => s?.bg || "transparent"),
      pointBorderColor: orphanStyles.map((s) => s?.border || "transparent"),
      pointBorderWidth: orphanStyles.map((s) => s?.borderW || 0),
      pointRadius: orphanStyles.map((s) => (s ? s.radius : 0)),
      pointStyle: orphanStyles.map((s) => s?.style || "circle"),
      pointHoverRadius: orphanStyles.map((s) => (s ? s.hover : 0)),
    });

    return {
      labels,
      datasets,
      expectedDatasetIndex,
      orphanDatasetIndex,
      hasBaselineData,
    };
  }, [dataPoints, gapAfterIndex, ruleCode, showBaseline, t]);

  const lineChartData = useMemo(
    () => ({ labels: chartBundle.labels, datasets: chartBundle.datasets }),
    [chartBundle.labels, chartBundle.datasets]
  );

  const buildExternalTooltip = useCallback(
    (context) => {
      const { chart, tooltip } = context;
      let el = chart.canvas.parentNode.querySelector(".mat-tooltip-box");
      if (!el) {
        el = document.createElement("div");
        el.className = "mat-tooltip-box";
        el.setAttribute("role", "tooltip");
        chart.canvas.parentNode.style.position = "relative";
        chart.canvas.parentNode.appendChild(el);
      }
      if (tooltip.opacity === 0) {
        el.style.display = "none";
        return;
      }
      const dp = tooltip.dataPoints?.[0];
      if (!dp) return;
      const index = dp.dataIndex;
      const point = dataPoints[index];
      const datasetIndex = dp.datasetIndex;
      const { expectedDatasetIndex } = chartBundle;

      el.style.display = "block";
      el.style.left = `${tooltip.caretX}px`;
      el.style.top = `${tooltip.caretY - 10}px`;
      el.innerHTML = "";

      const addRow = (key, val, valClass = "") => {
        const row = document.createElement("div");
        row.className = "tooltip-row";
        row.innerHTML = `<span class="tooltip-key">${key}</span><span class="tooltip-val ${valClass}">${val}</span>`;
        el.appendChild(row);
      };

      if (expectedDatasetIndex >= 0 && datasetIndex === expectedDatasetIndex) {
        addRow(t("AnomalyDetection.expectedValue.label"), dp.raw ?? "—");
        return;
      }

      addRow(t("AnomalyDetection.period.label"), point?.period ? formatTimelinePeriod(point.period) : dp.label);
      addRow(t("MetricAnomalyTimeline.reportedValue.label"), point?.value ?? "—");
      if (point?.hasAnomaly) {
        if (dataPointShowsExpectedBaseline(ruleCode, point)) {
          addRow(t("AnomalyDetection.expectedValue.label"), point.expectedValue ?? "—");
        }
        if (point.maxDeviationPct != null) {
          addRow(t("AnomalyDetection.deviationPct.label"), `${point.maxDeviationPct.toFixed(1)}%`);
        }
        addRow(t("AnomalyDetection.anomalyCount.label"), point.anomalyCount ?? "—");
        if (point.maxSeverity) {
          addRow(t("AnomalyDetection.severity.label"), formatAnomalySeverityLabel(point.maxSeverity));
        }
      }
    },
    [chartBundle, dataPoints, ruleCode, t]
  );

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      layout: {
        padding: {
          top: 18,
          right: 20,
          bottom: 6,
          left: 6,
        },
      },
      plugins: {
        datalabels: {
          display: false,
        },
        legend: {
          display: true,
          position: "top",
          align: "end",
          labels: {
            font: { family: "Inter, sans-serif", size: 11 },
            usePointStyle: true,
            filter: (item) =>
              item.text === t("AnomalyDetection.actualValue.label")
              || (chartBundle.hasBaselineData
                && item.text === t("AnomalyDetection.expectedValue.label")),
          },
        },
        tooltip: { enabled: false, external: buildExternalTooltip },
        matGapLabelPlugin: {
          gapMidpoints,
          label: t("AnomalyDetection.missingPeriod.label"),
        },
        matPointValuePlugin: {
          dataPoints,
          orphanDatasetIndex: chartBundle.orphanDatasetIndex,
        },
      },
      scales: {
        x: {
          offset: true,
          ticks: {
            padding: 14,
            autoSkip: dataPoints.length > 12,
            maxRotation: 0,
            font: (ctx) => ({
              size: 11,
              family: "Inter, sans-serif",
              weight: dataPoints[ctx.index]?.hasAnomaly ? "700" : "400",
            }),
            color: (ctx) => (dataPoints[ctx.index]?.hasAnomaly ? SEVERITY_COLORS.CRITICAL : "#64748b"),
          },
          grid: {
            drawTicks: false,
          },
        },
        y: {
          grace: "12%",
          ticks: {
            padding: 10,
            font: { size: 11, family: "Inter, sans-serif" },
          },
          grid: {
            drawTicks: false,
          },
          beginAtZero: false,
        },
      },
      elements: {
        point: {
          hitRadius: 14,
        },
      },
      interaction: { mode: "nearest", intersect: true },
      onClick: (event, elements) => {
        if (!elements.length) return;
        const { index, datasetIndex } = elements[0];
        const { expectedDatasetIndex, orphanDatasetIndex } = chartBundle;
        const clickable = new Set([0, orphanDatasetIndex]);
        if (expectedDatasetIndex >= 0) {
          clickable.add(expectedDatasetIndex);
        }
        if (!clickable.has(datasetIndex)) return;
        const point = dataPoints[index];
        if (!point) return;
        const isShift = event.native?.shiftKey;
        if (isShift && rangeAnchorPeriod) {
          onRangeSelect(point.period);
        } else {
          onPeriodSelect(point.period, isShift);
        }
      },
    }),
    [
      buildExternalTooltip,
      chartBundle,
      dataPoints,
      gapMidpoints,
      onPeriodSelect,
      onRangeSelect,
      rangeAnchorPeriod,
      t,
    ]
  );

  return (
    <div className="anomaly-chart-card">
      <div className="chart-card-header">
        <h6 className="chart-header-with-tip">
          {t("MetricAnomalyTimeline.storylineChart.label")}
          <AnomalyInfoTip text={t("AnomalyDetection.timeSeriesChartToolTip.label")} />
        </h6>
        <small className="chart-note-text">
          {t("MetricAnomalyTimeline.chartClickHint.label")}
        </small>
      </div>
      <div className="chart-card-body chart-body-relative">
        <div className="mat-chart-height">
          <Line data={lineChartData} options={chartOptions} />
        </div>
        <div className="mat-severity-legend">
          <span className="mat-legend-title">{t("AnomalyDetection.severityLegend.label")}</span>
          {SEVERITY_LEGEND.map((item) => (
            <span key={item.key} className={`mat-legend-item ${item.markerClass}`}>
              <span className="mat-legend-marker" />
              {t(`AnomalyDetection.${item.key.toLowerCase()}.label`)}
            </span>
          ))}
        </div>
       
      </div>
    </div>
  );
};

export default withTranslation()(MetricStorylineChart);
